// CTSK-322 — the Timeline Lane viz tab's CHROME PREFERENCES drawer.
//
// Was the CTSK-320 source picker. The dataset toggles moved OUT to the
// always-visible chip strip (TimelineVizChipStrip.tsx): hiding "which data
// is on the chart" behind a closed-by-default panel was the CTSK-320
// mistake this ticket reverses. What remains is the ⚙ target for layout
// and chrome preferences — currently empty, deliberately.
//
// Contract with the host (TimelineLane.tsx), unchanged from CTSK-320:
//   - ALWAYS MOUNTED. Open/close is a pure transform toggle
//     (translateX(100%) ↔ translateX(0)) so toggling the drawer never
//     restructures the sibling iframe's position in the tree — the viz
//     keeps its zoom/selection state. The host persists `isOpen` to
//     localStorage('timeline-lane:viz-chrome') and owns the state.
//   - `position: absolute; right/top/bottom: 0` — the host's wrapper (the
//     viz container) anchors it via position: relative (normal mode) or
//     position: fixed (the CTSK-262 fullscreen class), so the drawer works
//     identically in both modes.
//   - `children` is the slot where real preferences land.

import type { ReactNode } from 'react';
import { X } from 'lucide-react';

export interface TimelineVizSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Preference controls, once there are any. */
  children?: ReactNode;
}

export function TimelineVizSettingsDrawer({
  isOpen,
  onClose,
  children,
}: TimelineVizSettingsDrawerProps) {
  return (
    <div
      data-testid="viz-settings-drawer"
      role="complementary"
      aria-label="Chrome preferences"
      aria-hidden={!isOpen}
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 280,
        boxSizing: 'border-box',
        padding: '12px 14px',
        overflowY: 'auto',
        background: 'var(--surface-raised)',
        borderLeft: '1px solid var(--border-strong)',
        boxShadow: isOpen ? '-4px 0 14px rgba(0,0,0,0.12)' : 'none',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 180ms ease-out',
        // Closed = fully off-canvas; make that explicit for pointer + AT
        // semantics too (the transform alone still leaves the node
        // focusable in some UAs).
        pointerEvents: isOpen ? 'auto' : 'none',
        visibility: isOpen ? 'visible' : 'hidden',
        zIndex: 2,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          Chrome preferences
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chrome preferences"
          title="Close the chrome preferences drawer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: 4,
            border: 'none',
            borderRadius: 4,
            background: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
        >
          <X size={14} aria-hidden />
        </button>
      </div>

      {children ?? (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)' }}>
          No preferences yet — more controls will land here.
        </p>
      )}
    </div>
  );
}
